export type SovereignEntity = {
  id: string
  createdAt: string
  updatedAt: string
}

export interface Company extends SovereignEntity {
  name: string
  domain?: string
  website?: string
  industry?: string
  notes?: string
}

export interface Deal extends SovereignEntity {
  companyId: string
  title: string
  value: number
  currency: string
  stage: PipelineStage
  status: DealStatus
}

export type PipelineStage =
  | 'lead'
  | 'conversation'
  | 'proposal'
  | 'negotiation'
  | 'won'
  | 'lost'
  | 'nurture'

export type DealStatus = 'active' | 'stale' | 'archived'

export interface Contact extends SovereignEntity {
  companyId?: string
  name: string
  email?: string
  phone?: string
  role?: string
  notes?: string
}

export interface Activity extends SovereignEntity {
  entityType: EntityType
  entityId: string
  type: ActivityType
  content: string
}

export type EntityType = 'company' | 'deal' | 'contact'

export type ActivityType = 'call' | 'email' | 'note' | 'meeting'

export interface Task extends SovereignEntity {
  entityType?: EntityType
  entityId?: string
  title: string
  dueDate?: string
  completed: boolean
}

export const PIPELINE_STAGES: readonly PipelineStage[] = [
  'lead',
  'conversation',
  'proposal',
  'negotiation',
  'won',
  'lost',
  'nurture',
] as const

export const STAGE_LABELS: Record<PipelineStage, string> = {
  lead: 'Leads',
  conversation: 'Conversations',
  proposal: 'Proposals',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
  nurture: 'Nurture',
}

export const STAGE_EMPTY_TEXT: Record<PipelineStage, string> = {
  lead: 'No leads yet.',
  conversation: 'No conversations yet.',
  proposal: 'No proposals yet.',
  negotiation: 'No deals in negotiation.',
  won: 'No deals won yet.',
  lost: 'No lost deals.',
  nurture: 'No deals in nurture.',
}

export const ACTIVITY_LABELS: Record<ActivityType, string> = {
  call: 'Call',
  email: 'Email',
  note: 'Note',
  meeting: 'Meeting',
}

export const DEFAULT_CURRENCY = 'GBP'

export const CURRENCIES = ['GBP', 'USD', 'EUR', 'CAD', 'AUD'] as const
export type Currency = (typeof CURRENCIES)[number]

export interface GmailThread {
  id: string
  historyId: string
  snippet: string
  messages: GmailMessage[]
}

export interface GmailMessage {
  id: string
  threadId: string
  labelIds: string[]
  snippet: string
  payload: GmailMessagePayload
  internalDate: string
}

export interface GmailMessagePayload {
  headers: GmailHeader[]
  body?: GmailMessageBody
  parts?: GmailMessagePart[]
  mimeType: string
}

export interface GmailHeader {
  name: string
  value: string
}

export interface GmailMessageBody {
  size: number
  data?: string
}

export interface GmailMessagePart {
  partId: string
  mimeType: string
  filename: string
  headers: GmailHeader[]
  body: GmailMessageBody
  parts?: GmailMessagePart[]
}

export interface EmailThreadSummary {
  id: string
  subject: string
  participants: string[]
  snippet: string
  lastMessageDate: Date
  messageCount: number
  isUnread: boolean
}

export interface EmailMessage {
  id: string
  threadId: string
  from: string
  to: string[]
  cc: string[]
  subject: string
  date: Date
  snippet: string
  bodyText: string
  bodyHtml?: string
}

export const STAGE_COMPOSE_SUBJECTS: Record<PipelineStage, string> = {
  lead: 'Introduction',
  conversation: 'Following up on our conversation',
  proposal: 'Following up on the proposal',
  negotiation: 'Following up on our discussion',
  won: 'Thank you',
  lost: 'Checking in',
  nurture: 'Checking in',
}

// AI Provider Types
export type AIProvider = 'openai' | 'anthropic' | 'gemini' | 'openrouter'

export interface AIProviderConfig {
  name: string
  endpoint: string
  defaultModel: string
}

export interface AIProviderSettings {
  provider: AIProvider
  encryptedKey: string
  keyFingerprint: string
  updatedAt: string
}

export interface EncryptionSalt {
  salt: string
  createdAt: string
}

export interface AIMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface AICompletionOptions {
  model?: string
  timeoutMs?: number
  maxRetries?: number
}

export const AI_PROVIDERS: Record<AIProvider, { name: string; placeholder: string }> = {
  openai: { name: 'OpenAI', placeholder: 'sk-...' },
  anthropic: { name: 'Anthropic', placeholder: 'sk-ant-...' },
  gemini: { name: 'Gemini', placeholder: 'AIza...' },
  openrouter: { name: 'OpenRouter', placeholder: 'sk-or-...' },
}
